import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import { base_url, RECAPTCHA_SITE_KEY } from "constant/constant";
import ThankYouModal from "../ThankYouModal";
import { useLocale } from "i18n/locale";

type ContactFormModalProps = {
    isOpen: boolean;
    onClose: () => void;
    setFormValues?: any;
    formValues: any;
    setSnackbarSeverity?:any;
    setSnackbarMessage?:any;
    setSnackbarOpen ?:any;
    // Only the home-page contact form (SectionGridHasMap) shows the "Thank you"
    // modal on success; every other usage (e.g. the global footer) keeps the
    // success snackbar.
    showThankYouOnSuccess?: boolean;
};


type FormErrors = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    message?: string;
    recaptcha?: string;
};

const ContactFormModal: React.FC<ContactFormModalProps> = ({ isOpen, onClose, setFormValues, formValues, setSnackbarSeverity,setSnackbarMessage,setSnackbarOpen, showThankYouOnSuccess = false }) => {
    const { t } = useLocale();

    const [errors, setErrors] = useState<FormErrors>({});
    const [showThankYou, setShowThankYou] = useState(false);
    // First/last name are shown as two fields in the UI but the API still expects
    // a single `name`, so we keep them local and combine into formValues.name.
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    // reCAPTCHA token also lives in local state (not formValues) for the same
    // reason as first/last name — it needs to reset on close without waiting
    // on the parent's formValues reset.
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    // The reCAPTCHA "normal" widget is a fixed 304px-wide iframe. On narrow
    // screens (small phones, and the squeezed form column in the two-column
    // desktop/tablet layout) it overflows the white card and gets clipped on
    // the right. Measure the available width and scale the widget down to fit
    // when there isn't room for its full 304px.
    const RECAPTCHA_WIDTH = 304;
    const RECAPTCHA_HEIGHT = 78;
    const [recaptchaScale, setRecaptchaScale] = useState(1);
    // A callback ref (not useEffect/useLayoutEffect) so measurement runs the
    // instant the wrapper node is attached to the DOM — every time the modal
    // opens — rather than depending on effect timing relative to the modal's
    // open transition. clientWidth is the wrapper's own content width and is
    // unaffected by the transform the open animation briefly applies. A
    // ResizeObserver keeps it correct on rotation/resize.
    const roRef = useRef<ResizeObserver | null>(null);
    const setRecaptchaWrapper = useCallback((node: HTMLDivElement | null) => {
        roRef.current?.disconnect();
        roRef.current = null;
        if (!node) return;
        const measure = () => {
            const available = node.clientWidth;
            if (available > 0) {
                setRecaptchaScale(
                    available < RECAPTCHA_WIDTH ? available / RECAPTCHA_WIDTH : 1
                );
            }
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(node);
        roRef.current = ro;
    }, []);

    // Clears a field's validation error as soon as the user edits it, so the
    // "required" messages set on submit disappear the moment the field has a
    // value rather than lingering until the next submit.
    const clearError = (field: keyof FormErrors) => {
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormValues({ ...formValues, [name]: type === "checkbox" ? checked : value });
        // email/phone are the only text inputs routed through here that carry a
        // validation error; clear it once the field has a value.
        if ((name === "email" || name === "phone") && value.trim()) {
            clearError(name);
        }
    };

    const handleFirstName = (value: string) => {
        setFirstName(value);
        setFormValues({ ...formValues, name: `${value} ${lastName}`.trim() });
        if (value.trim()) clearError("firstName");
    };
    const handleLastName = (value: string) => {
        setLastName(value);
        setFormValues({ ...formValues, name: `${firstName} ${value}`.trim() });
        if (value.trim()) clearError("lastName");
    };
    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        setFormValues({ ...formValues, message: value });
        if (value.trim()) clearError("message");
    };

    // First/last name and validation errors live in local state (unlike the
    // rest of the form, which the parent resets via formValues). Without this,
    // closing the modal without submitting (e.g. clicking outside) leaves
    // stale first/last name and error messages showing next time it opens.
    useEffect(() => {
        if (!isOpen) {
            setFirstName("");
            setLastName("");
            setErrors({});
            setRecaptchaToken(null);
            recaptchaRef.current?.reset();
        }
    }, [isOpen]);


    // Returns true on a successful inquiry so the caller can show the
    // "Thank you" modal. Errors still surface via the snackbar.
    const InquiryForm = async (): Promise<boolean> => {
        try {
            const inquryRes = await axios.post(`${base_url}/website/inquiry_form_website`, { ...formValues, recaptchaToken });
            if (!inquryRes.data.error) {
                setFormValues({ name: "",
                    email: "",
                    phone: "",
                    message: "",
                    isChecked: false})
                return true;
            }
            else {
                setFormValues({ name: "",
                    email: "",
                    phone: "",
                    message: "",
                    isChecked: false})
                setSnackbarSeverity('error');
                setSnackbarMessage(inquryRes.data.title);
                setSnackbarOpen(true);
                return false;
            }
        } catch (err) {
            setSnackbarSeverity('error');
            setSnackbarMessage(t("contactForm.somethingWentWrong"));
            setSnackbarOpen(true);
            return false;
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: FormErrors = {};

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!firstName.trim()) {
            newErrors.firstName = t("contactForm.firstNameRequired");
        }
        if (!lastName.trim()) {
            newErrors.lastName = t("contactForm.lastNameRequired");
        }
        if (!formValues.email.trim()) {
            newErrors.email = t("reservationForm.emailRequired");
        } else if (!emailRegex.test(formValues.email.trim())) {
            newErrors.email = t("reservationForm.emailInvalid");
        }
        if (!formValues.message?.trim()) {
            newErrors.message = t("contactForm.messageRequired");
        }
        if (!recaptchaToken) {
            newErrors.recaptcha = t("contactForm.recaptchaRequired");
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            setErrors({});
            const ok = await InquiryForm();
            setFirstName("");
            setLastName("");
            onClose();
            if (ok) {
                if (showThankYouOnSuccess) {
                    setShowThankYou(true);
                } else {
                    setSnackbarSeverity('success');
                    setSnackbarMessage(t("contactForm.submitSuccess"));
                    setSnackbarOpen(true);
                }
            }
        }
    };


    const personIcon = (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
        </svg>
    );
    const envelopeIcon = (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
        </svg>
    );
    const phoneIcon = (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 5 5L16 13l5 2v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
        </svg>
    );

    return (
        <>
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
                <div className="min-h-screen text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40 dark:bg-opacity-60" />
                    </Transition.Child>

                    <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                    </span>
                    <Transition.Child
                        className="inline-block py-6 px-2 sm:px-4 w-full max-w-[860px]"
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div
                            className="relative inline-flex w-full text-left align-middle transition-all transform overflow-hidden bg-[#FAF6F0] shadow-xl rounded-md"
                            style={{ fontFamily: "Poppins, sans-serif" }}
                        >
                            {/* Close button — mobile only. The modal fills the screen on
                                mobile, so tapping the backdrop isn't reachable there and this
                                is the only way out. From md up the backdrop is visible and
                                clickable (plus Esc), so the icon is hidden per client request. */}
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label={t("common.close")}
                                className="absolute top-3 right-3 z-10 flex md:hidden h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#01152E] shadow-sm border border-[#EFE7DA] hover:bg-white transition-colors"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                            <div className="grid w-full grid-cols-1 md:grid-cols-2">
                                {/* LEFT — brand panel (desktop only): logo sits top-left, then
                                    a tall photo (flush to the card's left edge) beside the copy.
                                    The photo's top stays below the logo; pb-6 lines its bottom
                                    up with the form card in the right column. */}
                                <div className="hidden md:flex md:flex-col">
                                    {/* Logo — top-left, as it was before */}
                                    <img src="/logo/europea-residences-group.svg" alt="Europea Residences" className="h-11 w-auto self-start ml-6 mt-6" />
                                    <div className="mt-4 grid flex-1 grid-cols-[44%_1fr] gap-5 pb-6 pr-4">
                                        {/* Photo — flush to the card's left border; bottom aligns
                                            with the form card, top sits below the logo. */}
                                        <img src="https://s3.us-east-1.amazonaws.com/europea-staging/prod/property_gallery/Living%20Room/655f37e728fd1c30f9403e33/19289638_compress.jpeg" alt="" className="h-full w-full object-cover rounded-r-md" />
                                        {/* Copy column */}
                                        <div className="flex flex-col pt-4 pl-2">
                                            <p className="text-[11px] text-center font-semibold uppercase tracking-[2px] text-[#B88333]">{t("footer.contactUs")}</p>
                                            <h3 className="mt-1 text-[30px] leading-[38px] font-semibold text-[#01152E]">{t("contactForm.hereToHelp")}</h3>
                                            <div className="my-3 h-[2px] w-10 bg-[#B88333]" />
                                            <p className="text-[13px] leading-[20px] text-[#445162]">{t("contactForm.haveQuestion")}</p>
                                            <div className="mt-8 flex flex-col items-center gap-1.5 rounded-md bg-[#F6EFE4] px-4 py-4 text-center">
                                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B88333" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" />
                                                    <path d="M9 12l2 2 4-4" />
                                                </svg>
                                                <p className="text-[12px] leading-[18px] text-[#445162]">{t("contactForm.infoSecure")}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT — form inside a white card floating on the cream.
                                    The extra top padding starts the card below the logo row
                                    (rather than level with it), matching the design. */}
                                <div className="p-5 md:pl-4 md:pt-12 md:pr-6 md:pb-6">
                                  <div className="rounded-md bg-white p-6 shadow-sm border border-[#EFE7DA]">
                                    <Dialog.Title as="h3" className="text-[18px] font-semibold text-[#01152E] mb-4">
                                        {t("contactForm.sendMessage")}
                                    </Dialog.Title>
                                    <form onSubmit={handleSubmit} className="space-y-3">
                                        {/* First + Last name */}
                                        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
                                            <div>
                                                <div className="relative">
                                                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#98A2B3]">{personIcon}</span>
                                                    <input
                                                        type="text"
                                                        value={firstName}
                                                        onChange={(e) => handleFirstName(e.target.value)}
                                                        className={`block w-full pl-10 pr-3 py-2 text-[14px] text-[#01152E] !rounded-lg border ${errors.firstName ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus:!border-[#01152E] focus:!ring-0 focus:outline-none`}
                                                    />
                                                    {!firstName && (
                                                        <span className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-[14px] text-[#98A2B3] whitespace-nowrap">
                                                            {t("contactForm.firstName")} <span className="text-[#E5484D]">*</span>
                                                        </span>
                                                    )}
                                                </div>
                                                {errors.firstName && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.firstName}</p>}
                                            </div>
                                            <div>
                                                <div className="relative">
                                                    <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#98A2B3]">{personIcon}</span>
                                                    <input
                                                        type="text"
                                                        value={lastName}
                                                        onChange={(e) => handleLastName(e.target.value)}
                                                        className={`block w-full pl-10 pr-3 py-2 text-[14px] text-[#01152E] !rounded-lg border ${errors.lastName ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus:!border-[#01152E] focus:!ring-0 focus:outline-none`}
                                                    />
                                                    {!lastName && (
                                                        <span className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-[14px] text-[#98A2B3] whitespace-nowrap">
                                                            {t("contactForm.lastName")} <span className="text-[#E5484D]">*</span>
                                                        </span>
                                                    )}
                                                </div>
                                                {errors.lastName && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.lastName}</p>}
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <div className="relative">
                                                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#98A2B3]">{envelopeIcon}</span>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    value={formValues.email || ""}
                                                    onChange={handleChange}
                                                    className={`block w-full pl-10 pr-3 py-2 text-[14px] text-[#01152E] !rounded-lg border ${errors.email ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus:!border-[#01152E] focus:!ring-0 focus:outline-none`}
                                                />
                                                {!(formValues.email) && (
                                                    <span className="pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-[14px] text-[#98A2B3]">
                                                        {t("reservationForm.emailAddress")} <span className="text-[#E5484D]">*</span>
                                                    </span>
                                                )}
                                            </div>
                                            {errors.email && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.email}</p>}
                                        </div>

                                        {/* Phone — label + helper text sit inside the field */}
                                        <div>
                                            <div className={`flex gap-2.5 px-3.5 py-2 !rounded-lg border ${errors.phone ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus-within:!border-[#01152E]`}>
                                                <span className="mt-0.5 text-[#98A2B3]">{phoneIcon}</span>
                                                <div className="flex-1">
                                                    <div className="text-[14px] text-[#475467]">{t("reservationForm.phoneNumber")}</div>
                                                    <input
                                                        type="text"
                                                        name="phone"
                                                        id="phone"
                                                        value={formValues.phone || ""}
                                                        onChange={handleChange}
                                                        placeholder={t("contactForm.phonePlaceholder")}
                                                        className="block w-full bg-transparent border-0 !rounded-none p-0 text-[14px] text-[#01152E] placeholder:text-[#98A2B3] focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            {errors.phone && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.phone}</p>}
                                        </div>

                                        {/* Message — label + placeholder sit inside the field */}
                                        <div>
                                            <div className={`flex gap-2.5 px-3.5 py-2 !rounded-lg border ${errors.message ? "!border-[#e07a7a]" : "!border-[#d6d6d6]"} focus-within:!border-[#01152E]`}>
                                                <span className="mt-0.5 text-[#98A2B3]">
                                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                                                    </svg>
                                                </span>
                                                <div className="flex-1">
                                                    <div className="text-[14px] text-[#475467]">{t("contactForm.yourMessage")} <span className="text-[#E5484D]">*</span></div>
                                                    <textarea
                                                        id="message"
                                                        rows={2}
                                                        value={formValues.message || ""}
                                                        onChange={handleTextareaChange}
                                                        placeholder={t("contactForm.messagePlaceholder")}
                                                        className="block w-full bg-transparent border-0 !rounded-none p-0 mt-0.5 text-[14px] text-[#01152E] placeholder:text-[#98A2B3] resize-none focus:ring-0 focus:outline-none"
                                                    />
                                                </div>
                                            </div>
                                            {errors.message && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.message}</p>}
                                        </div>

                                        {/* Anti-spam check */}
                                        <div>
                                            <p className="text-[13px] font-semibold text-[#01152E] mb-2">{t("contactForm.antiSpamCheck")}</p>
                                            {/* Wrapper measures the available width; the inner box is
                                                scaled so the fixed-width widget never overflows the card.
                                                Its height tracks the scaled height so no gap is left below. */}
                                            <div ref={setRecaptchaWrapper} className="overflow-hidden">
                                                <div
                                                    style={{
                                                        width: RECAPTCHA_WIDTH,
                                                        height: RECAPTCHA_HEIGHT * recaptchaScale,
                                                        transform: `scale(${recaptchaScale})`,
                                                        transformOrigin: "0 0",
                                                    }}
                                                >
                                                    <ReCAPTCHA
                                                        ref={recaptchaRef}
                                                        sitekey={RECAPTCHA_SITE_KEY}
                                                        onChange={(token) => {
                                                            setRecaptchaToken(token);
                                                            if (token) clearError("recaptcha");
                                                        }}
                                                        onExpired={() => setRecaptchaToken(null)}
                                                    />
                                                </div>
                                            </div>
                                            {errors.recaptcha && <p className="text-[#e05a5a] text-[12px] mt-1">{errors.recaptcha}</p>}
                                        </div>

                                        {/* Send */}
                                        <button
                                            type="submit"
                                            className="relative mt-1 flex w-full items-center justify-center rounded-lg bg-[#B88333] px-6 py-3 text-[14px] font-semibold uppercase tracking-[1px] text-white hover:opacity-90"
                                        >
                                            {t("contactForm.sendMessageButton")}
                                            <svg className="absolute right-6" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12h14" />
                                                <path d="m13 6 6 6-6 6" />
                                            </svg>
                                        </button>
                                    </form>
                                  </div>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
        <ThankYouModal isOpen={showThankYou} onClose={() => setShowThankYou(false)} />
        </>
    );
};

export default ContactFormModal;
